import { RefObject, useRef } from 'react'

import { PointCloudOctree, Potree } from '@datalabs-jp/three-loader'
import { RootState, useFrame } from '@react-three/fiber'
import mixpanel from 'mixpanel-browser'
import { Points, RGBAFormat, Scene, UnsignedByteType, WebGLRenderTarget } from 'three'

import { useBlueprintEditorContext } from '../contexts/BlueprintEditorContext'
import { Tools } from '../tools/setup'

export const BLUEPRINT_RENDER_LOOP_FLAGS = {
  showGrid: true,
  buffer: null as Uint8Array | null,
}

const RenderLoop = ({
  scene,
  potree,
  pointClouds,
}: {
  scene: RefObject<Scene | null>
  potree: Potree
  pointClouds: (PointCloudOctree | Points | null)[]
}) => {
  const { activeCamera } = useBlueprintEditorContext()

  // Collect tools with useFrame hooks
  const toolHooks = Tools.map((tool) => tool.hooks?.useFrame).filter(Boolean) as ((props: RootState) => void)[]

  // Performance tracking refs
  const frameCount = useRef(0)
  const lastFpsReport = useRef(Date.now())
  const fpsSamples = useRef<number[]>([])
  const nextMilestoneIndex = useRef(0)
  const milestones = useRef([1, 3, 10])

  const potreePcds = pointClouds.filter((pc) => pc instanceof PointCloudOctree)

  useFrame((props) => {
    const { gl, clock } = props

    if (!scene.current || !activeCamera.current) return
    const { clientWidth, clientHeight } = gl.domElement
    gl.setScissor(0, 0, clientWidth, clientHeight)
    gl.setViewport(0, 0, clientWidth, clientHeight)

    // Run tool frame hooks
    toolHooks.forEach((hook) => hook(props))

    // Exporting the current frame to buffer
    if (!BLUEPRINT_RENDER_LOOP_FLAGS.showGrid && !BLUEPRINT_RENDER_LOOP_FLAGS.buffer) {
      const canvas3d = document.querySelector('#canvasBox canvas')
      const grid = scene.current.getObjectByName('canvas-grid')
      if (grid && canvas3d instanceof HTMLCanvasElement) {
        grid.visible = false

        const originalTarget = gl.getRenderTarget()
        const target = new WebGLRenderTarget(canvas3d.width, canvas3d.height, {
          format: RGBAFormat,
          type: UnsignedByteType,
          depthBuffer: true,
          stencilBuffer: false,
          samples: 4,
        })

        // Render to target and read pixels into buffer
        BLUEPRINT_RENDER_LOOP_FLAGS.buffer = new Uint8Array(canvas3d.width * canvas3d.height * 4)
        gl.setRenderTarget(target)
        gl.clear(true, true, true)
        gl.render(scene.current, activeCamera.current)
        gl.readRenderTargetPixels(target, 0, 0, canvas3d.width, canvas3d.height, BLUEPRINT_RENDER_LOOP_FLAGS.buffer)

        // Restore state
        grid.visible = true
        gl.setRenderTarget(originalTarget)
        target.dispose()
      }
    }

    // Update point clouds via Potree
    if (potreePcds.length > 0) {
      potree.updatePointClouds(potreePcds, activeCamera.current, gl)
    }

    // Render the scene
    gl.render(scene.current, activeCamera.current)

    // Track rendering performance
    frameCount.current += 1
    const now = Date.now()
    const timeSinceLastReport = now - lastFpsReport.current

    // Calculate FPS every second
    if (timeSinceLastReport >= 1000) {
      const fps = (frameCount.current / timeSinceLastReport) * 1000

      // Collect FPS sample
      fpsSamples.current.push(fps)

      frameCount.current = 0
      lastFpsReport.current = now
    }

    // Report performance at specific milestones: 1 min, 3 min, and 10 min
    // Only check the next milestone to avoid unnecessary iterations
    if (nextMilestoneIndex.current < milestones.current.length && fpsSamples.current.length > 0) {
      const sessionTimeInMinutes = clock.elapsedTime / 60
      const nextMilestone = milestones.current[nextMilestoneIndex.current]

      if (sessionTimeInMinutes >= nextMilestone) {
        const averageFps = fpsSamples.current.reduce((sum, fps) => sum + fps, 0) / fpsSamples.current.length

        // Calculate 1% low FPS (average of lowest 1% of frames)
        const sortedFps = [...fpsSamples.current].sort((a, b) => a - b)
        const onePercentCount = Math.max(1, Math.ceil(sortedFps.length * 0.01))
        const onePercentLowFps =
          sortedFps.slice(0, onePercentCount).reduce((sum, fps) => sum + fps, 0) / onePercentCount

        const maxFps = Math.max(...fpsSamples.current)

        mixpanel.track('Rendering Performance', {
          'Average FPS': Math.round(averageFps),
          '1% Low FPS': Math.round(onePercentLowFps),
          'Max FPS': Math.round(maxFps),
          'Point Clouds Count': pointClouds.filter((pc) => pc !== null).length,
          'Session Time (min)': nextMilestone,
        })

        nextMilestoneIndex.current += 1
      }
    }
  }, 1)

  return null
}

export default RenderLoop
